import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button } from 'components/UIElements'
import PdfSignerModal from './PdfSignerModal'
import { SIGNER_UI_STATES } from '../../../utils/pdfSignerTypes'
import { stampDcfPdf } from '../../../utils/stampDcfPdf'
import {
  getDcfSignaturePlacement,
  isClickInDcfSignatureHotspot,
} from '../../../utils/dcfSignerRole'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const PAGE_RENDER_WIDTH = 640

/**
 * PDF preview with click-to-place e-Sign.
 * Stamp role is resolved by the parent (single mapped role, or next unsigned
 * for Root / DCH Admin cycle). Clicks are limited to that role's hotspot.
 */
const PdfViewerWithSigning = ({
  pdfBytes,
  canSign = false,
  savedRoles = [],
  signingRole = null,
  signingRoleLabel = '',
  requiresDecision = false,
  roleSourceNote = '(from your account)',
  prefill = null,
  onSigned,
  verifyPassword,
}) => {
  const placement = useMemo(() => getDcfSignaturePlacement(signingRole), [signingRole])
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(placement?.page || 1)
  const [pendingPlacement, setPendingPlacement] = useState(null)
  const [decision, setDecision] = useState('approved')
  const [uiState, setUiState] = useState(SIGNER_UI_STATES.IDLE)
  const [errorText, setErrorText] = useState('')
  const [fileUrl, setFileUrl] = useState(null)
  const pageInnerRef = useRef(null)

  const roleAlreadySigned = Boolean(signingRole && savedRoles.includes(signingRole))
  const canPlaceStamp = canSign && Boolean(signingRole) && !roleAlreadySigned

  useEffect(() => {
    if (!pdfBytes) {
      setFileUrl(null)
      return undefined
    }
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    setFileUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pdfBytes])

  useEffect(() => {
    if (placement?.page) setPageNumber(placement.page)
  }, [placement?.page])

  useEffect(() => {
    setPendingPlacement(null)
  }, [pageNumber])

  useEffect(() => {
    setPendingPlacement(null)
    setErrorText('')
    setUiState(SIGNER_UI_STATES.IDLE)
  }, [signingRole])

  const onPageClick = event => {
    if (!canPlaceStamp || uiState === SIGNER_UI_STATES.STAMPING) return
    if (!signingRole || !placement) {
      setErrorText(
        canSign && !signingRole
          ? 'All signature blocks for your account are already signed on this DCF.'
          : 'Your account role is not mapped to a DCF signature block.',
      )
      return
    }
    if (roleAlreadySigned) {
      setErrorText(`${signingRoleLabel || 'This role'} is already signed on this DCF.`)
      return
    }

    const canvas = pageInnerRef.current?.querySelector('canvas')
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const xPct = ((event.clientX - rect.left) / rect.width) * 100
    const yPct = ((event.clientY - rect.top) / rect.height) * 100
    if (xPct < 0 || xPct > 100 || yPct < 0 || yPct > 100) {
      setErrorText('Click inside the page to place the stamp.')
      return
    }

    if (!isClickInDcfSignatureHotspot(signingRole, { page: pageNumber, xPct, yPct })) {
      setErrorText(
        `Click inside the ${signingRoleLabel || 'assigned'} signature area on page ${
          placement.page
        } (beside Signature and Date).`,
      )
      return
    }

    setErrorText('')
    // Snap to the role's fixed stamp zone so content lands beside/above the label.
    const { hotspot } = placement
    setPendingPlacement({
      page: placement.page,
      xPct: (hotspot.xMinPct + hotspot.xMaxPct) / 2,
      yPct: hotspot.yMaxPct,
    })
  }

  const placeStampAtHotspot = () => {
    if (!canPlaceStamp || uiState === SIGNER_UI_STATES.STAMPING) return
    if (!signingRole || !placement) {
      setErrorText(
        canSign && !signingRole
          ? 'All signature blocks for your account are already signed on this DCF.'
          : 'Your account role is not mapped to a DCF signature block.',
      )
      return
    }
    if (roleAlreadySigned) {
      setErrorText(`${signingRoleLabel || 'This role'} is already signed on this DCF.`)
      return
    }
    if (pageNumber !== placement.page) {
      setPageNumber(placement.page)
      return
    }
    setErrorText('')
    const { hotspot } = placement
    setPendingPlacement({
      page: placement.page,
      xPct: (hotspot.xMinPct + hotspot.xMaxPct) / 2,
      yPct: hotspot.yMaxPct,
    })
  }

  const onPageKeyDown = event => {
    if (!canPlaceStamp || uiState === SIGNER_UI_STATES.STAMPING) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    placeStampAtHotspot()
  }

  const onConfirmSign = async form => {
    if (!pendingPlacement || !pdfBytes || !signingRole) return
    setUiState(SIGNER_UI_STATES.REAUTHENTICATING)
    setErrorText('')
    try {
      const reauth = await verifyPassword(form.password)
      setUiState(SIGNER_UI_STATES.STAMPING)
      const signedAtIso = new Date().toISOString()
      const hasDrawnMark = Boolean(form.signatureData)
      // One value for both the stamp and the record so they cannot disagree.
      const signedDecision = requiresDecision ? form.decision || decision : undefined
      const stamped = await stampDcfPdf(pdfBytes, {
        pngDataUrl: form.signatureData,
        name: form.signerName,
        title: form.signerTitle,
        signedAtIso,
        role: signingRole,
        page: pendingPlacement.page,
        xPct: pendingPlacement.xPct,
        yPct: pendingPlacement.yPct,
        decision: signedDecision,
      })
      await onSigned({
        role: signingRole,
        signerName: form.signerName,
        signerTitle: form.signerTitle,
        signerEmail: form.signerEmail,
        signatureType: hasDrawnMark ? 'draw' : 'text',
        signatureData: hasDrawnMark ? form.signatureData : form.signerName,
        decision: signedDecision,
        placement: pendingPlacement,
        reauthProof: reauth.reauth_proof,
        reauthJti: reauth.jti,
        reauthVerifiedAt: signedAtIso,
        stampedPdfBytes: stamped,
      })
      setPendingPlacement(null)
      setUiState(SIGNER_UI_STATES.COMPLETED)
    } catch (err) {
      setUiState(SIGNER_UI_STATES.ERROR)
      setErrorText(err?.message || 'Signing failed')
    }
  }

  if (!fileUrl) {
    return <div className='dcf-esign-empty'>Loading PDF…</div>
  }

  let toolbarHint = placement
    ? `Click inside the highlighted ${signingRoleLabel} area on page ${placement.page}.`
    : 'Click the signature line on the page — the stamp starts at the click point.'
  if (!signingRole) {
    toolbarHint = canSign
      ? 'All signature blocks available to your account are already signed.'
      : 'Your account role is not mapped to a DCF signature block.'
  } else if (roleAlreadySigned) {
    toolbarHint = `${signingRoleLabel} is already signed on this DCF.`
  }

  const showHotspot =
    canPlaceStamp && placement && pageNumber === placement.page && placement.hotspot

  return (
    <div className='dcf-esign-viewer'>
      {canSign && (
        <div className='dcf-esign-toolbar'>
          <span className='dcf-esign-role-fixed'>
            Signing as <strong>{signingRoleLabel || '—'}</strong>
            <span className='dcf-esign-role-note'> {roleSourceNote}</span>
          </span>
          <span className='dcf-esign-hint'>{toolbarHint}</span>
        </div>
      )}
      {errorText && uiState !== SIGNER_UI_STATES.REAUTHENTICATING && (
        <div className='dcf-esign-error'>
          <i className='fas fa-exclamation-circle' /> {errorText}
        </div>
      )}
      <div className='dcf-esign-page-nav'>
        <Button
          secondary
          content='Prev'
          disabled={pageNumber <= 1}
          onClick={() => setPageNumber(n => Math.max(1, n - 1))}
        />
        <span>
          Page {pageNumber} / {numPages || '…'}
        </span>
        <Button
          secondary
          content='Next'
          disabled={!numPages || pageNumber >= numPages}
          onClick={() => setPageNumber(n => Math.min(numPages, n + 1))}
        />
        {placement && pageNumber !== placement.page && canPlaceStamp && (
          <Button
            secondary
            content={`Go to ${signingRoleLabel} area`}
            onClick={() => setPageNumber(placement.page)}
          />
        )}
      </div>
      <div className={`dcf-esign-page-wrap${canPlaceStamp ? ' clickable' : ''}`}>
        <div
          className='dcf-esign-page-inner'
          ref={pageInnerRef}
          role='button'
          tabIndex={0}
          aria-disabled={!canPlaceStamp}
          aria-label='Click the assigned signature area to place the stamp'
          onClick={onPageClick}
          onKeyDown={onPageKeyDown}
        >
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages: next }) => setNumPages(next)}
            loading={<div>Rendering PDF…</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={PAGE_RENDER_WIDTH}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          {showHotspot && (
            <span
              className='dcf-esign-hotspot'
              style={{
                left: `${placement.hotspot.xMinPct}%`,
                top: `${placement.hotspot.yMinPct}%`,
                width: `${placement.hotspot.xMaxPct - placement.hotspot.xMinPct}%`,
                height: `${placement.hotspot.yMaxPct - placement.hotspot.yMinPct}%`,
              }}
            />
          )}
          {pendingPlacement && pendingPlacement.page === pageNumber && (
            <span
              className='dcf-esign-placement-marker'
              style={{
                left: `${pendingPlacement.xPct}%`,
                top: `${pendingPlacement.yPct}%`,
              }}
            />
          )}
        </div>
      </div>
      <PdfSignerModal
        open={Boolean(pendingPlacement)}
        roleLabel={signingRoleLabel}
        requiresDecision={requiresDecision}
        decision={decision}
        onDecisionChange={setDecision}
        busy={
          uiState === SIGNER_UI_STATES.REAUTHENTICATING || uiState === SIGNER_UI_STATES.STAMPING
        }
        errorText={uiState === SIGNER_UI_STATES.ERROR ? errorText : ''}
        initialSignerName={prefill?.signerName || ''}
        initialSignerTitle={prefill?.signerTitle || ''}
        initialSignerEmail={prefill?.signerEmail || ''}
        onCancel={() => {
          setPendingPlacement(null)
          setUiState(SIGNER_UI_STATES.IDLE)
          setErrorText('')
        }}
        onConfirm={onConfirmSign}
      />
    </div>
  )
}

PdfViewerWithSigning.propTypes = {
  pdfBytes: PropTypes.oneOfType([
    PropTypes.instanceOf(Uint8Array),
    PropTypes.instanceOf(ArrayBuffer),
  ]),
  canSign: PropTypes.bool,
  savedRoles: PropTypes.arrayOf(PropTypes.string),
  signingRole: PropTypes.string,
  signingRoleLabel: PropTypes.string,
  requiresDecision: PropTypes.bool,
  roleSourceNote: PropTypes.string,
  prefill: PropTypes.shape({
    signerName: PropTypes.string,
    signerTitle: PropTypes.string,
    signerEmail: PropTypes.string,
  }),
  onSigned: PropTypes.func,
  verifyPassword: PropTypes.func,
}

export default PdfViewerWithSigning
